import { useCallback, useState, useEffect, FC, useMemo } from "react";
import {
  Box,
  HStack,
  Stack,
  Text,
  Heading,
  Button,
  Flex,
  Image,
} from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import useEmblaCarousel from "embla-carousel-react";

import CustomLink from "components/Chakra/CustomLink";
import { IconHeart, IconHeartBorder } from "@ud/icons";
import { Offer } from "@ud/api/newPhpApi/__generated__/newPhpApi.generated";
import { EApiConvenienceId, formatLocalitySearch } from "utils/offer";
import FavoriteButton from "components/FavoriteButton/FavoriteButton";
import { useTracking } from "contexts/Tracking";
import RentalPrice from "components/RentalPrice";

const ListingPreview: FC<{
  offer: Offer;
}> = ({ offer }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel();
  const [scrollSnaps, setScrollSnaps] = useState<Array<number>>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const { trackEvent } = useTracking();

  const limitedPhotos = offer.photos.slice(0, 5);

  const tags = useMemo(() => {
    const items: Array<string> = [];

    if (offer?.isNoCommission) {
      items.push("Bez provize");
    }

    return items;
  }, [offer]);

  const amenities = useMemo(() => {
    const items: Array<string> = [];

    if (
      offer.convenience &&
      offer.convenience.includes(EApiConvenienceId.BALCONY)
    ) {
      items.push("Balkón");
    }

    if (
      offer.convenience &&
      offer.convenience.includes(EApiConvenienceId.CELLAR)
    ) {
      items.push("Sklep");
    }

    return items;
  }, [offer]);

  const onSelect = useCallback(() => {
    if (!emblaApi) {
      return;
    }
    setSelectedIndex(emblaApi.selectedScrollSnap);
  }, [emblaApi, setSelectedIndex]);

  useEffect(() => {
    if (!emblaApi) {
      return;
    }
    onSelect();
    setScrollSnaps(emblaApi.scrollSnapList);
    emblaApi.on("select", onSelect);
  }, [emblaApi, setScrollSnaps, onSelect]);

  const scrollTo = useCallback(
    (index: number) => emblaApi && emblaApi.scrollTo(index),
    [emblaApi],
  );

  return (
    <Box position="relative">
      {/* Tag & Heart Button */}
      <HStack
        justifyContent="space-between"
        position="absolute"
        left={0}
        px={4}
        top={4}
        zIndex={2}
        w="100%"
      >
        {tags.length > 0 && (
          <Box
            px={2}
            height="24px"
            bgColor="ud-white"
            fontWeight="bold"
            border="1px solid #CCCCCC"
            borderRadius="12px"
          >
            {tags.map((tag, tagId) => (
              <Text variant="small_2" color="ud-black-900" key={tagId}>
                {tag}
              </Text>
            ))}
          </Box>
        )}

        <Box ml="auto" mt="4px">
          <FavoriteButton
            offerId={offer.id}
            loginEvent={{
              LOGIN: {
                "dl.eAct": "login",
                "dl.eLab": "Uložit inzerát – výpis",
                "dl.eCat": "",
              },
              REGISTRATION: {
                "dl.eAct": "registration",
                "dl.eLab": "Uložit inzerát – výpis",
                "dl.eCat": "",
              },
            }}
            onAdd={() => {
              const eCat = "listing";
              trackEvent({
                "dl.eCat": eCat,
                "dl.eAct": "favs.clicked",
                "dl.eLab": "Uložit",
              });
            }}
            onRemove={() => {
              const eCat = "listing";
              trackEvent({
                "dl.eCat": eCat,
                "dl.eAct": "favs.clicked",
                "dl.eLab": "Odebrat",
              });
            }}
          >
            {(isInFavorite) =>
              isInFavorite ? (
                <Button
                  className="group"
                  w="40px"
                  h="40px"
                  borderRadius="full"
                  backgroundColor="rgba(255, 255, 255, 0.50)"
                  backdropFilter="blur(8px)"
                  _hover={{
                    backgroundColor: "rgba(255, 255, 255, 0.70)",
                  }}
                >
                  <IconHeart
                    fontSize="26px"
                    fill="ud-primary"
                    _groupHover={{ fill: "ud-primary" }}
                  />
                </Button>
              ) : (
                <Button
                  className="group"
                  w="40px"
                  h="40px"
                  borderRadius="full"
                  backgroundColor="rgba(255, 255, 255, 0.50)"
                  backdropFilter="blur(8px)"
                  _hover={{
                    backgroundColor: "rgba(255, 255, 255, 0.70)",
                  }}
                >
                  <IconHeartBorder fontSize="26px" fill="white" />
                </Button>
              )
            }
          </FavoriteButton>
        </Box>
      </HStack>

      <Box
        position="relative"
        borderRadius="16px"
        overflow="hidden"
        background="ud-grey-100"
        role="group"
      >
        <Box overflow="hidden" ref={emblaRef}>
          <Flex className="embla__container" h="325px">
            {limitedPhotos.map((photo, photoId) => (
              <Box
                className="embla__slide"
                minW="100%" // important: make each slide full width
                h="325px"
                key={photoId}
                style={{ aspectRatio: "1 / 1" }}
                position="relative"
                flexShrink={0}
              >
                <Image
                  src={photo.path}
                  alt={`Listing ${photoId}`}
                  objectFit="cover"
                  w="100%"
                  h="100%"
                />
              </Box>
            ))}
          </Flex>
        </Box>

        {limitedPhotos.length > 1 && (
          <Flex
            position="absolute"
            bottom={3}
            left={0}
            right={0}
            justifyContent="center"
            alignItems="center"
          >
            <Flex
              justifyContent="center"
              alignItems="center"
              borderRadius="full"
              bg="rgba(211, 211, 211, 0)"
              backdropFilter="blur(0px)"
              px={2}
              py={1}
              _groupHover={{
                bg: "rgba(211, 211, 211, 0.20)",
                backdropFilter: "blur(8px)",
                "&>.arrowWrapper": { opacity: 1 },
              }}
              transition="all 0.3s ease"
            >
              <Box
                className="arrowWrapper"
                opacity={0}
                transition="opacity 0.3s ease"
              >
                <ChevronLeftIcon
                  boxSize="24px"
                  mr={2}
                  color="white"
                  cursor={selectedIndex === 0 ? "default" : "pointer"}
                  opacity={selectedIndex === 0 ? 0.3 : 1}
                  transition="opacity 0.3s ease"
                  onClick={() => emblaApi?.scrollPrev()}
                />
              </Box>

              {scrollSnaps.map((_, idx) => (
                <Button
                  p={0}
                  w="5px"
                  minWidth="5px"
                  h="5px"
                  mx="4px"
                  transform={idx === selectedIndex ? "scale(1.6)" : "scale(1"}
                  transition="transform 0.3s ease"
                  bgColor="white"
                  _hover={{ backgroundColor: "white" }}
                  key={idx}
                  onClick={() => scrollTo(idx)}
                />
              ))}

              <Box
                className="arrowWrapper"
                opacity={0}
                transition="opacity 0.3s ease"
              >
                <ChevronRightIcon
                  boxSize="24px"
                  ml={2}
                  color="white"
                  cursor={
                    selectedIndex === limitedPhotos.length - 1
                      ? "default"
                      : "pointer"
                  }
                  opacity={selectedIndex === limitedPhotos.length - 1 ? 0.3 : 1}
                  transition="opacity 0.3s ease"
                  onClick={() => emblaApi?.scrollNext()}
                />
              </Box>
            </Flex>
          </Flex>
        )}
      </Box>

      {/* Listing Details */}
      <CustomLink
        to="/inzerat/[seo]/[id]"
        routeProps={{ seo: offer.seo, id: offer.id.toString() }}
        display="block"
        trackEventPayload={{
          "dl.eCat": "homepage",
          "dl.eAct": "offer.clicked",
          "dl.eLab": "Zobrazit inzerát",
        }}
        prefetch={false}
      >
        <Box
          role="group"
          transition="opacity 0.3s ease"
          _hover={{ opacity: 0.5 }}
        >
          <Stack pt={4}>
            <Heading as="h3" fontSize="16px" color="ud-black-900">
              {offer.title}
            </Heading>

            <Text variant="small_2" color="ud-black-900">
              {formatLocalitySearch(offer)}
            </Text>

            <HStack alignItems="center" flexWrap="wrap">
              <Text
                fontSize="24px"
                fontWeight="600"
                color="black"
                whiteSpace="nowrap"
              >
                {offer.rentalPrice ? (
                  <RentalPrice
                    rentalPrice={offer.rentalPrice}
                    offerTypeId="rent"
                  />
                ) : (
                  <Text as="span" fontSize="inherit">
                    Cena u makléře
                  </Text>
                )}
              </Text>
            </HStack>

            {amenities.length > 0 && (
              <HStack spacing={5} pt="1">
                {amenities.map((item, index, arr) => (
                  <Box key={index} position="relative" color="ud-black-900">
                    {item}
                    {index !== arr.length - 1 && (
                      <Box
                        position="absolute"
                        right="-12px"
                        top="50%"
                        transform="translateY(-50%)"
                        width="3px"
                        height="3px"
                        bg="ud-black-900"
                        borderRadius="full"
                      />
                    )}
                  </Box>
                ))}
              </HStack>
            )}
          </Stack>
        </Box>
      </CustomLink>
    </Box>
  );
};

export default ListingPreview;
